import { animate, style, transition, trigger } from '@angular/animations';
import { Component, Inject, OnInit } from '@angular/core';
import { NavigationEnd, NavigationError, Router } from '@angular/router';
import { MissingRefreshTokenError } from '@auth0/auth0-angular';
import { CognitoAuthService } from '@techspert-io/auth';
import { RAY_GUN, Raygun } from '@techspert-io/errorHandling';
import { NotificationService } from '@techspert-io/notifications';
import moment from 'moment';
import { Observable, interval, merge, of } from 'rxjs';
import { delay, filter, switchMap, take, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  animations: [
    trigger('enterAnimation', [
      transition(':enter', [
        style({ transform: 'translateY(100%)', opacity: 1, bottom: -15 }),
        animate('1000ms', style({ transform: 'translateY(0)', bottom: -15 })),
      ]),
      transition(':leave', [
        animate(
          '700ms',
          style({ transform: 'translateY(100%)', bottom: -100 })
        ),
      ]),
    ]),
  ],
  standalone: false,
})
export class AppComponent implements OnInit {
  showSanta$: Observable<boolean>;
  isInitialNavigation = true;

  constructor(
    private router: Router,
    private cognitoAuthService: CognitoAuthService,
    private notificationService: NotificationService,
    @Inject(RAY_GUN) private raygun: Raygun
  ) {}

  ngOnInit(): void {
    this.router.events
      .pipe(
        // Guards redirect via router.navigate() then return false, which fires
        // NavigationCancel for the rejected route before the replacement
        // navigation completes — only NavigationEnd/NavigationError signal the
        // app has actually settled on a route.
        filter(
          (event) =>
            event instanceof NavigationEnd || event instanceof NavigationError
        ),
        take(1)
      )
      .subscribe(() => {
        this.isInitialNavigation = false;
      });

    moment.updateLocale('en', {
      monthsShort: [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sept',
        'Oct',
        'Nov',
        'Dec',
      ],
    });

    this.notificationService.initPoll().subscribe();

    this.cognitoAuthService.authError$.subscribe((err) => {
      if (
        err instanceof MissingRefreshTokenError ||
        err.message === 'Unknown or invalid refresh token.'
      ) {
        this.cognitoAuthService.logOut();
      } else {
        this.router.navigate(['error'], {
          queryParams: { message: err.message },
        });
      }
    });

    this.cognitoAuthService
      .initAuth()
      .pipe(takeUntil(this.cognitoAuthService.authError$))
      .subscribe((user) => {
        if (user) {
          this.handleRedirect();
          this.raygun('setUser', {
            identifier: user.email,
            isAnonymous: false,
            uuid: user.user_id,
          });
          this.showTheSanta(user.email);
        }
      });
  }

  showTheSanta(email: string): void {
    this.showSanta$ = interval(190000).pipe(
      filter(() => moment().month() === 11),
      filter(() => !['stuart.tottle@techspert.com'].includes(email)),
      switchMap(() => merge(of(true), of(false).pipe(delay(3400))))
    );
  }

  private handleRedirect(): void {
    const redirectUrl = this.cognitoAuthService.getRedirectUrl();
    this.cognitoAuthService.setRedirectUrl(null);

    if (redirectUrl) {
      this.router.navigate([redirectUrl], {
        replaceUrl: true,
      });
    }
  }
}
